import NiceModal from '@ebay/nice-modal-react'
import CloseIcon from '@mui/icons-material/Close'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Select,
  SortDirection,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { Permission } from '@shared/types/roles.types'
import {
  Workspace,
  WorkspaceStatus,
  WorkspaceUser
} from '@shared/types/workspaces.types'
import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import EmptyFolder from '../../assets/images/EmptyFolder.svg'
import ContainedAvatar from '../../components/ContainedAvatar'
import EmptySearchResults from '../../components/EmptySearchResults'
import SearchInput from '../../components/forms/SearchInput'
import TableLoader from '../../components/loaders/TableLoader'
import { AbandonWorkspaceModal } from '../../components/modals/AbandonWorkspaceModal'
import { ChangeWorkspaceStatusModal } from '../../components/modals/ChangeWorkspaceStatusModal'
import { DeleteWorkspaceModal } from '../../components/modals/DeleteWorkspaceModal'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useCompanyUser } from '../../hooks/useQueries/useCompanies'
import { useWorkspaces } from '../../hooks/useQueries/useWorkspaces'
import { parseDateFromISO } from '../../utils/date.utils'

type CompaniesWorkspacesSortColumn =
  | 'name'
  | 'clientName'
  | 'clientCode'
  | 'startDate'
  | 'endDate'
  | 'status'
  | ''

export default function CompaniesWorkspaces() {
  const { t } = useTranslation()
  const params = useParams()
  const companyId = params.companyId!
  const { data: workspaces, isLoading: isWorkspacesLoading } =
    useWorkspaces(companyId)
  const { userId } = useAuthStore()
  const { data: user, isLoading: isCompanyUserLoading } = useCompanyUser(
    companyId,
    userId!
  )
  const [searchText, setSearchText] = useState('')
  const [status, setStatus] = useState('')
  const [orderBy, setOrderBy] = useState<CompaniesWorkspacesSortColumn>('')
  const [orderDirection, setOrderDirection] = useState<SortDirection>('asc')
  const [filteredWorkspaces, setFilteredWorkspaces] = useState<
    (Workspace & { users: WorkspaceUser[] })[]
  >([])
  const [lastAppliedFilters, setLastAppliedFilters] = useState({
    searchText: '',
    status: ''
  })
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  useEffect(() => {
    setFilteredWorkspaces(workspaces || [])
  }, [workspaces])

  const canCreateWorkspaces = useMemo(() => {
    return (
      user?.permissions?.includes(Permission.WORKSPACES_CREATE) ||
      user?.role?.fullAccess
    )
  }, [user])

  const canUpdateWorkspaces = useMemo(() => {
    return (
      user?.permissions?.includes(Permission.WORKSPACES_UPDATE) ||
      user?.role?.fullAccess
    )
  }, [user])

  const canDeleteWorkspaces = useMemo(() => {
    return (
      user?.permissions?.includes(Permission.WORKSPACES_DELETE) ||
      user?.role?.fullAccess
    )
  }, [user])

  const handleSort = (column: CompaniesWorkspacesSortColumn) => {
    let newOrderDirection: SortDirection = 'asc'

    if (orderBy === column) {
      newOrderDirection = orderDirection === 'asc' ? 'desc' : 'asc'
    }

    setOrderBy(column)
    setOrderDirection(newOrderDirection)

    const sorted = [...filteredWorkspaces].sort((a, b) => {
      if (column === 'startDate' || column === 'endDate') {
        const getDateValue = (
          workspace: Workspace & { users: WorkspaceUser[] }
        ) => {
          const date =
            column === 'startDate' ? workspace.startDate : workspace.endDate
          return date ? new Date(date).getTime() : 0
        }
        const valA = getDateValue(a)
        const valB = getDateValue(b)

        return newOrderDirection === 'asc' ? valA - valB : valB - valA
      } else {
        const getStringValue = (
          workspace: Workspace & { users: WorkspaceUser[] }
        ) => {
          switch (column) {
            case 'name':
              return workspace.name.toLowerCase()
            case 'clientName':
              return workspace.clientName.toLowerCase()
            case 'clientCode':
              return workspace.clientCode?.toLowerCase() || ''
            case 'status':
              return workspace.status.toLowerCase()
            default:
              return ''
          }
        }
        const valA = getStringValue(a)
        const valB = getStringValue(b)

        return newOrderDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
    })

    setFilteredWorkspaces(sorted)
  }

  const handleFilter = (searchValue: string, statusValue: string) => {
    const result = workspaces?.filter(workspace => {
      const matchSearch = JSON.stringify(workspace)
        .toLowerCase()
        .includes(searchValue.toLowerCase())

      const matchStatus = statusValue ? workspace.status === statusValue : true

      return matchSearch && matchStatus
    })

    setFilteredWorkspaces(result || [])
    setLastAppliedFilters({ searchText: searchValue, status: statusValue })
  }

  const handleMenuClick = (
    event: MouseEvent<HTMLElement>,
    workspace: Workspace
  ) => {
    setAnchorEl(event.currentTarget)
    setSelectedWorkspace(workspace)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedWorkspace(null)
  }

  const areFiltersEqual = (
    current: typeof lastAppliedFilters,
    last: typeof lastAppliedFilters
  ) => {
    return (
      current.searchText === last.searchText && current.status === last.status
    )
  }

  if (isWorkspacesLoading || isCompanyUserLoading) {
    return <TableLoader totalRows={4} totalColumns={7} />
  }

  return (
    <>
      <TableContainer>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          paddingY='1rem'
        >
          <Stack
            direction='row'
            spacing='1rem'
            alignItems='center'
            paddingBottom='1rem'
          >
            <SearchInput
              searchText={searchText}
              setSearchText={setSearchText}
              handleFilter={handleFilter}
              additionalParams={[status]}
            />
            <Select
              value={status}
              onChange={e => setStatus(e.target.value)}
              sx={{
                width: '13.75rem',
                '& .MuiSelect-select': {
                  padding: '.75rem .75rem'
                }
              }}
              displayEmpty
              renderValue={selected => {
                if (selected === '') {
                  return (
                    <Typography color='text.secondary'>
                      {t('common.status')}
                    </Typography>
                  )
                }
                return t(`workspaces.status.${selected.toLowerCase()}`)
              }}
            >
              {Object.values(WorkspaceStatus).map(status => (
                <MenuItem value={status} key={status}>
                  {t(`workspaces.status.${status}`)}
                </MenuItem>
              ))}
            </Select>

            <Button
              variant='text'
              color='primary'
              onClick={() => handleFilter(searchText, status)}
              disabled={
                (!searchText && !status) ||
                areFiltersEqual({ searchText, status }, lastAppliedFilters)
              }
              sx={{ textTransform: 'none' }}
            >
              {t('common.apply_filters')}
            </Button>
            {(searchText || status) && (
              <Button
                startIcon={<CloseIcon />}
                onClick={() => {
                  setSearchText('')
                  setStatus('')
                  setFilteredWorkspaces(workspaces || [])
                  setLastAppliedFilters({
                    searchText: '',
                    status: ''
                  })
                }}
                sx={{
                  textTransform: 'none',
                  color: 'secondary.main'
                }}
              >
                {t('common.delete_filters')}
              </Button>
            )}
          </Stack>
          {canCreateWorkspaces && (
            <Button
              variant='text'
              color='primary'
              startIcon={<EditNoteOutlinedIcon />}
              component={Link}
              to={`/companies/${companyId}/workspaces/create`}
              sx={{ textTransform: 'none' }}
            >
              {t('workspaces.create_button')}
            </Button>
          )}
        </Stack>
        {workspaces?.length ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={orderBy === 'name' ? orderDirection : false}
                >
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={
                      orderBy === 'name' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('name')}
                  >
                    {t('workspaces.table_headers.workspace')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderBy === 'clientName' ? orderDirection : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === 'clientName'}
                    direction={
                      orderBy === 'clientName' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('clientName')}
                  >
                    {t('workspaces.table_headers.client')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderBy === 'clientCode' ? orderDirection : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === 'clientCode'}
                    direction={
                      orderBy === 'clientCode' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('clientCode')}
                  >
                    {t('workspaces.table_headers.code')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderBy === 'startDate' ? orderDirection : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === 'startDate'}
                    direction={
                      orderBy === 'startDate' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('startDate')}
                  >
                    {t('workspaces.table_headers.start_date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'endDate' ? orderDirection : false}
                >
                  <TableSortLabel
                    active={orderBy === 'endDate'}
                    direction={
                      orderBy === 'endDate' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('endDate')}
                  >
                    {t('workspaces.table_headers.end_date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'status' ? orderDirection : false}
                >
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={
                      orderBy === 'status' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('status')}
                  >
                    {t('workspaces.table_headers.status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align='right'>
                  {t('workspaces.table_headers.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWorkspaces.length > 0 ? (
                filteredWorkspaces.map(workspace => {
                  const isWorkspaceMember = workspace.users.some(
                    workspaceUser => workspaceUser.userId === user?.userId
                  )

                  return (
                    <TableRow key={workspace.id}>
                      <TableCell>
                        <Stack
                          direction='row'
                          spacing='.5rem'
                          alignItems='center'
                        >
                          {workspace.logoUri ? (
                            <ContainedAvatar
                              src={workspace.logoUri}
                              alt={workspace.name}
                              sx={{ width: '2rem', height: '2rem' }}
                            />
                          ) : (
                            <Avatar sx={{ width: '2rem', height: '2rem' }}>
                              {workspace.name.charAt(0).toUpperCase()}
                            </Avatar>
                          )}
                          <Typography variant='body2'>
                            {workspace.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>
                          {workspace.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>
                          {workspace.clientCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>
                          {parseDateFromISO(workspace.startDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>
                          {parseDateFromISO(workspace.endDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(`workspaces.status.${workspace.status}`)}
                          sx={{
                            ...(WorkspaceStatus.ACTIVE === workspace.status && {
                              backgroundColor: 'success.dark',
                              color: 'primary.contrastText'
                            }),
                            ...(WorkspaceStatus.CANCELED ===
                              workspace.status && {
                              backgroundColor: 'error.main',
                              color: '#fff'
                            }),
                            ...(WorkspaceStatus.PAUSED === workspace.status && {
                              backgroundColor: 'warning.dark',
                              color: 'primary.contrastText'
                            }),
                            ...(WorkspaceStatus.FINISHED ===
                              workspace.status && {
                              backgroundColor: 'info.dark',
                              color: '#fff'
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Stack
                          direction='row'
                          spacing='.75rem'
                          justifyContent='flex-end'
                        >
                          {isWorkspaceMember && (
                            <Button
                              variant='contained'
                              disabled={
                                workspace.status !== WorkspaceStatus.ACTIVE
                              }
                              component={Link}
                              to={`/companies/${companyId}/workspaces/${workspace.id}/digital-twin`}
                              sx={{
                                textTransform: 'none',
                                fontSize: '.875rem',
                                padding: '.375rem 1rem',
                                minWidth: 'auto'
                              }}
                            >
                              {t('common.enter')}
                            </Button>
                          )}
                          {isWorkspaceMember && (
                            <IconButton
                              onClick={event =>
                                handleMenuClick(event, workspace)
                              }
                              sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.12)'
                              }}
                            >
                              <MoreHorizIcon color='action' />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptySearchResults />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
              <MenuList>
                {canUpdateWorkspaces && (
                  <MenuItem
                    onClick={() => {
                      if (selectedWorkspace) {
                        NiceModal.show(ChangeWorkspaceStatusModal, {
                          companyId,
                          workspaceId: selectedWorkspace.id,
                          workspaceName: selectedWorkspace.name,
                          currentStatus: selectedWorkspace.status
                        })
                      }
                      handleCloseMenu()
                    }}
                  >
                    <ListItemIcon>
                      <SwapHorizOutlinedIcon fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>
                      {t('workspaces.menu.change_status')}
                    </ListItemText>
                  </MenuItem>
                )}
                {canUpdateWorkspaces && (
                  <MenuItem
                    component={Link}
                    to={`/companies/${companyId}/workspaces/${selectedWorkspace?.id}/edit`}
                    onClick={handleCloseMenu}
                  >
                    <ListItemIcon>
                      <CreateOutlinedIcon fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>{t('common.edit')}</ListItemText>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    if (selectedWorkspace) {
                      NiceModal.show(AbandonWorkspaceModal, {
                        companyId,
                        workspace: selectedWorkspace
                      })
                    }
                    handleCloseMenu()
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>{t('common.abandon')}</ListItemText>
                </MenuItem>
                {canDeleteWorkspaces && (
                  <MenuItem
                    onClick={() => {
                      if (selectedWorkspace) {
                        NiceModal.show(DeleteWorkspaceModal, {
                          companyId,
                          workspaceId: selectedWorkspace.id
                        })
                      }
                      handleCloseMenu()
                    }}
                  >
                    <ListItemIcon>
                      <DeleteOutlineOutlinedIcon
                        fontSize='small'
                        color='error'
                      />
                    </ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>
                      {t('common.delete')}
                    </ListItemText>
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </Table>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '1rem',
              marginTop: '4rem'
            }}
          >
            <img
              src={EmptyFolder}
              alt='Empty Folder'
              style={{
                width: '12.5rem',
                height: '12.5rem',
                objectFit: 'contain'
              }}
            />
            <Typography variant='h6' fontWeight={700} color='text.primary'>
              {t('workspaces.empty.title')}
            </Typography>
            <Typography variant='body1' color='text.primary' textAlign='center'>
              {t('workspaces.empty.description')}
            </Typography>
            {canCreateWorkspaces && (
              <Button
                variant='contained'
                startIcon={<EditNoteOutlinedIcon />}
                component={Link}
                to={`/companies/${companyId}/workspaces/create`}
                sx={{
                  padding: '.5rem 1.375rem',
                  textTransform: 'none',
                  marginTop: '1rem'
                }}
              >
                {t('workspaces.empty.button')}
              </Button>
            )}
          </Box>
        )}
      </TableContainer>
    </>
  )
}
